import { supabase } from '@/integrations/supabase/client';
import { KINFEED_PAGE_SIZE, type FeedCommentPreview } from '../../shared/community';

export type KinfeedPage = {
  posts: any[];
  likes: any[];
  reactions: any[];
  recentComments: FeedCommentPreview[];
  hasMore: boolean;
};

export const fetchKinfeedPage = async ({
  userId,
  mode,
  offset,
  pageSize = KINFEED_PAGE_SIZE,
}: {
  userId: string;
  mode: 'local' | 'latest';
  offset: number;
  pageSize?: number;
}): Promise<KinfeedPage> => {
  const { data: viewerProfile } = await supabase
    .from('my_private_profile')
    .select('country_code')
    .maybeSingle();
  const { data: pageRows, error } = await supabase.rpc('get_kinfeed_page' as any, {
    p_mode: mode,
    p_country_code: viewerProfile?.country_code ?? null,
    p_offset: offset,
    p_limit: pageSize + 1,
  });

  if (error) throw error;
  const pagePosts = (pageRows ?? []).map((row: any) => row.post).filter(Boolean);
  const hasMore = pagePosts.length > pageSize;
  const posts = pagePosts.slice(0, pageSize);
  const postIds = posts.map((post: any) => post.id);

  if (!postIds.length) {
    return { posts, likes: [], reactions: [], recentComments: [], hasMore };
  }

  const [{ data: likes, error: likesError }, { data: reactions, error: reactionsError }, { data: comments, error: commentsError }] = await Promise.all([
    supabase.from('feed_post_likes').select('id, post_id, user_id').in('post_id', postIds),
    supabase.from('feed_post_reactions').select('id, post_id, user_id, emoji').in('post_id', postIds),
    supabase
      .from('feed_comments')
      .select('id, post_id, content, created_at, user_id, parent_id, profiles(display_name, avatar_url)')
      .eq('is_deleted', false)
      .in('post_id', postIds)
      .order('created_at', { ascending: false })
      .limit(pageSize * 3),
  ]);

  if (likesError) throw likesError;
  if (reactionsError) throw reactionsError;
  if (commentsError) throw commentsError;

  return {
    posts,
    likes: likes ?? [],
    reactions: reactions ?? [],
    recentComments: (comments ?? []) as FeedCommentPreview[],
    hasMore,
  };
};

export const createKinfeedComment = (postId: string, userId: string, content: string, parentId?: string | null) =>
  supabase.from('feed_comments').insert({
    post_id: postId,
    user_id: userId,
    content: content.trim(),
    parent_id: parentId ?? null,
  });

export const toggleKinfeedPostReaction = (postId: string, emoji: string) =>
  supabase.rpc('toggle_feed_post_reaction' as any, {
    p_post_id: postId,
    p_emoji: emoji,
  });

export const toggleKinfeedCommentReaction = (commentId: string, emoji: string) =>
  supabase.rpc('toggle_feed_comment_reaction' as any, {
    p_comment_id: commentId,
    p_emoji: emoji,
  });
